import { autoUpdate, computePosition, flip, offset, type Placement } from "@floating-ui/dom";

/**
 * Default behaviour:
 *  placement bottom
 *  auto-update on (update on resize, scroll,...) 
 *  no auto-placement, but flip on overflow (e.g. bottom<->top or right<->left but NOT bottom<->left)
 *  no offset
 *  no max-width enforcement
 *  no hide on outside click
 * @param referenceElement Reference of HTML Element or VueComponentInstance
 * @param floatingElement Reference of HTML Element or VueComponentInstance
 */
export function useSmartPositioning(
  referenceElement: Ref<HTMLElement | { $el: HTMLElement } | null>,
  floatingElement: Ref<HTMLElement | { $el: HTMLElement } | null>,
  options: {
    setMaxWidth: boolean,
    hideOnOutsideClick?: boolean,
    hideFunction?: () => void,
    computeOptions: {
      placement: Placement | undefined,
      middleware: any[],
    }
  } = {
    setMaxWidth: false,
    hideOnOutsideClick: false,
    computeOptions: {
      placement: 'bottom',
      middleware: [flip()],
    }
  }
) {
  const getHTMLElement = (el: any): HTMLElement | null => {
    if (!el) return null
    return el instanceof HTMLElement ? el : el.$el instanceof HTMLElement ? el.$el : null
  }

  const computedElements = computed(() => {
    return {
      referenceElement: getHTMLElement(referenceElement.value),
      floatingElement: getHTMLElement(floatingElement.value),
    }
  })

  const updatePosition = async () => {
    const { referenceElement, floatingElement } = computedElements.value;
    if (!referenceElement || !floatingElement) {
      return;
    }

    const { x, y } = await computePosition(
      referenceElement,
      floatingElement,
      options.computeOptions
    );
    floatingElement.style.left = `${x}px`;
    floatingElement.style.top = `${y}px`;
    floatingElement.classList.remove('hidden');
    if (options?.setMaxWidth) {
      floatingElement.style.maxWidth = `${referenceElement.getBoundingClientRect().width}px`;
    }
  };

  const getSmartPositionWithAutoUpdate = async (): Promise<Ref<() => void>> => {
    return new Promise((resolve) => {
      // this timeout is needed cause floating elements are often not in DOM when this is called
      // unfortunately nextTick does not work here
      setTimeout(() => {
        const { referenceElement, floatingElement } = computedElements.value;
        if (!referenceElement || !floatingElement) {
          return;
        }

        resolve(
          shallowRef(
            autoUpdate(
              referenceElement,
              floatingElement,
              updatePosition
            )
          )
        )
      }, 0)
    })
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (!options.hideFunction) {
      return
    }
    const { floatingElement, referenceElement } = computedElements.value
    if (!floatingElement || !referenceElement) {
      return
    }

    if (
      !floatingElement.contains(event.target as Node) &&
      !referenceElement.contains(event.target as Node)
    ) {
      options.hideFunction();
    }
  }

  if (getCurrentInstance()) {
    // we check both since in future we might call hideFunction somewhere additionally not just on outside click  
    if (options.hideOnOutsideClick && options.hideFunction) {
      document.addEventListener('click', handleOutsideClick);
    }
    onBeforeUnmount(() => {
      if (!options.hideFunction) {
        return
      }
      document.removeEventListener('click', options.hideFunction);
    })  
  }


  return { getSmartPositionWithAutoUpdate };
}